import StarRating from '@/features/leads/StarRating'
import { ModalShell, ModalHead, ModalSection, ModalLabel, ModalFoot } from '@/ui/Modal'
import { RATING_TIER, RATING_REASONS } from '@/shared/domain'
import { Textarea } from '@/ui'

export interface RatingDraft {
  leadId: string
  company: string
  rating: number
  comment: string
}

interface Props {
  draft: RatingDraft
  setDraft: (d: RatingDraft) => void
  onCancel: () => void
  onSave: () => void
}

// Append a reason to existing comment text. Skip if already present.
function appendReason(current: string, phrase: string): string {
  const trimmed = current.trim()
  if (trimmed.toLowerCase().includes(phrase.toLowerCase())) return current
  if (!trimmed) return phrase
  return trimmed.endsWith('.') || trimmed.endsWith('·') ? `${trimmed} ${phrase}` : `${trimmed} · ${phrase}`
}

export default function RatingModal({ draft, setDraft, onCancel, onSave }: Props) {
  // Show the "not a fit" reasons when the rating is 1★, otherwise the
  // positive set. Falls back to positive when no rating yet so the chips
  // are still useful while typing.
  const reasons = draft.rating === 1 ? RATING_REASONS.notfit : RATING_REASONS.positive
  return (
    <ModalShell onClose={onCancel} narrow>
      <ModalHead eyebrow="Betygsätt lead" title={draft.company} onClose={onCancel} />

      <ModalSection>
        <ModalLabel>Hur stark är den här leaden?</ModalLabel>
        <div className="flex items-center gap-4">
          <StarRating
            value={draft.rating}
            onChange={r => setDraft({ ...draft, rating: r })}
            size={32}
          />
          <span className="text-sm font-semibold text-ink-1000">
            {RATING_TIER[draft.rating] || 'Klicka på en stjärna för att betygsätta'}
          </span>
        </div>
      </ModalSection>

      <ModalSection>
        <ModalLabel>Kommentar (valfritt)</ModalLabel>
        <div className="flex flex-wrap gap-1 mb-2">
          {reasons.map(r => (
            <button
              key={r}
              type="button"
              className="px-2 py-1 text-[11px] text-ink-500 bg-transparent border border-ink-200 rounded-[2px] cursor-pointer transition-colors hover:bg-ink-50 hover:text-ink-1000 active:bg-ink-100"
              onClick={() => setDraft({ ...draft, comment: appendReason(draft.comment, r) })}
            >
              + {r}
            </button>
          ))}
        </div>
        <Textarea
          rows={3}
          placeholder="Varför det här betyget? Köpsignaler, invändningar, matchning…"
          value={draft.comment}
          onChange={e => setDraft({ ...draft, comment: e.target.value })}
          autoFocus
        />
      </ModalSection>

      <ModalFoot
        onCancel={onCancel}
        onSave={onSave}
        saveDisabled={!draft.rating}
        saveLabel="Spara betyg"
      />
    </ModalShell>
  )
}
